import React, { FC, memo, useCallback, useMemo, useState } from "react";
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { pricingResource, pricingResourceItem } from "../../../api/endpoints";
import { ContentChannel, usePricing } from "./PricingContext";
import { SnapshotBanner } from "./SnapshotBanner";
import { DotLottleLoader } from "../../../components/Loader";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { titleizeName } from "../../../utils";
import ContentChannelsList from "./components/ContentChannelsList";
import ContentChannelModal from "./components/ContentChannelModal";

interface CellProps {
    contentTypeId: number;
    channelId: number;
    initialValue: string;
    busy: boolean;
    disabled: boolean;
    onCommit: (contentTypeId: number, channelId: number, raw: string) => void;
}

const inputStyle: React.CSSProperties = {
    width: 80,
    padding: "6px 8px",
    border: "1px solid var(--border)",
    borderRadius: 6,
    fontFamily: "CircularStd, sans-serif",
    fontSize: 14
};

const Cell = memo<CellProps>(({ contentTypeId, channelId, initialValue, busy, disabled, onCommit }) => {
    return (
        <input
            type="number"
            defaultValue={initialValue}
            disabled={busy || disabled}
            placeholder="—"
            style={inputStyle}
            onBlur={(e) => onCommit(contentTypeId, channelId, e.target.value)}
        />
    );
});
Cell.displayName = "ContentTypeChannelCell";

export const ChannelsTab: FC = () => {
    const { data, contentTypes, contentChannels, loading, refetch, canEdit, canEditChannelDefaults } =
        usePricing();
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [editingChannel, setEditingChannel] = useState<ContentChannel | null>(null);
    const [channelToDelete, setChannelToDelete] = useState<ContentChannel | null>(null);

    const rows = data?.content_type_channel_prices;

    const { sortedTypes, sortedChannels, lookup } = useMemo(() => {
        const lk = new Map<string, { id: number; price: number }>();
        for (const row of rows ?? []) {
            lk.set(`${row.content_type_id}:${row.content_channel_id}`, {
                id: row.id,
                price: Number(row.base_price)
            });
        }
        // Types without "Portal Enabled" (expert_connect) are archived — keep
        // them out of the base-prices matrix and the per-channel counts.
        const portalEnabled = contentTypes.filter((ct) => ct.expert_connect !== false);
        const portalEnabledIds = new Set(portalEnabled.map((ct) => ct.id));
        return {
            sortedTypes: [...portalEnabled].sort((a, b) => a.name.localeCompare(b.name)),
            sortedChannels: [...contentChannels]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((ch) => ({
                    ...ch,
                    content_types: Array.isArray(ch.content_types)
                        ? ch.content_types.filter((ct) => portalEnabledIds.has(ct.id))
                        : ch.content_types
                })),
            lookup: lk
        };
    }, [rows, contentTypes, contentChannels]);

    const handleCellSave = useCallback(
        async (contentTypeId: number, channelId: number, raw: string) => {
            if (raw === "") return;
            const v = Number(raw);
            if (!Number.isFinite(v)) return;
            const key = `${contentTypeId}:${channelId}`;
            const existing = lookup.get(key);
            if (existing && existing.price === v) return;
            setBusyKey(key);
            try {
                if (existing) {
                    await axiosInstant.patch(pricingResourceItem("content-type-channel-prices", existing.id), {
                        base_price: v
                    });
                } else {
                    await axiosInstant.post(pricingResource("content-type-channel-prices"), {
                        content_type_id: contentTypeId,
                        content_channel_id: channelId,
                        base_price: v
                    });
                }
                toast.success("Saved");
                await refetch();
            } catch (e: any) {
                toast.error(e?.response?.data?.error ?? "Failed to save");
            } finally {
                setBusyKey(null);
            }
        },
        [lookup, refetch]
    );

    const handleSaveChannel = async (payload: any) => {
        try {
            if (editingChannel?.id) {
                await axiosInstant.patch(`/content-channels/${editingChannel.id}`, payload);
                toast.success("Channel updated.");
            } else {
                await axiosInstant.post("/content-channels", payload);
                toast.success("Channel created.");
            }
            setShowModal(false);
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Error saving channel.");
            throw e;
        }
    };

    const handleDeleteChannel = async () => {
        if (!channelToDelete) return;
        const id = channelToDelete.id;
        setChannelToDelete(null);
        try {
            await axiosInstant.delete(`/content-channels/${id}`);
            toast.success("Deleted successfully.");
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Error deleting channel.");
        }
    };

    if (loading || !data) return <DotLottleLoader />;

    return (
        <Box className="flex flex-col gap-8">
            <Box>
                <Box className="flex flex-wrap items-center justify-between gap-3" sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Content Channels
                    </Typography>
                    {canEdit && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setEditingChannel(null);
                                setShowModal(true);
                            }}
                        >
                            Add Channel
                        </Button>
                    )}
                </Box>
                <ContentChannelsList
                    channels={sortedChannels}
                    // Fulfillment can open the modal to set option defaults;
                    // add/delete stay with pricing editors.
                    onEdit={
                        canEditChannelDefaults
                            ? (channel: ContentChannel) => {
                                  setEditingChannel(channel);
                                  setShowModal(true);
                              }
                            : undefined
                    }
                    onDelete={canEdit ? (channel: ContentChannel) => setChannelToDelete(channel) : undefined}
                    loading={loading}
                />
            </Box>

            <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                    Channel Base Prices (per Content Type)
                </Typography>
                <SnapshotBanner />
                <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <strong>Content Type \ Channel</strong>
                                </TableCell>
                                {sortedChannels.map((ch) => (
                                    <TableCell key={ch.id} align="center">
                                        <strong>{ch.name}</strong>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedTypes.map((ct) => (
                                <TableRow key={ct.id}>
                                    <TableCell>{titleizeName(ct.name)}</TableCell>
                                    {sortedChannels.map((ch) => {
                                        const key = `${ct.id}:${ch.id}`;
                                        const existing = lookup.get(key);
                                        return (
                                            <TableCell key={ch.id}>
                                                <Cell
                                                    contentTypeId={ct.id}
                                                    channelId={ch.id}
                                                    initialValue={existing ? String(existing.price) : ""}
                                                    busy={busyKey === key}
                                                    disabled={!canEdit}
                                                    onCommit={handleCellSave}
                                                />
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <ContentChannelModal
                open={showModal}
                onSave={handleSaveChannel}
                onClose={() => setShowModal(false)}
                initialData={editingChannel || {}}
                defaultsOnly={!canEdit}
            />

            <ConfirmationDialog
                open={!!channelToDelete}
                onClose={() => setChannelToDelete(null)}
                onConfirm={handleDeleteChannel}
                title="Delete Channel"
                description="Are you sure you want to delete this channel?"
                confirmLabel="Delete"
            />
        </Box>
    );
};

export default ChannelsTab;
